import {
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { MemberSelect } from '@/features/members';
import { formatMoney } from '@/lib/money';
import { useSession } from '@/stores/session';
import { BASE as COMMISSIONS_BASE } from '../api';
import {
  COMMISSION_STATUSES,
  commissionStatusLabel,
  commissionStatusVariant,
} from '../constants';
import {
  useCommissions,
  useCommissionSummary,
  useDeleteCommission,
  useMarkCommissionPaidOut,
  useVoidCommission,
} from '../queries';
import { useCommissionsUi, type CommissionsViewMode } from '../store/viewPrefs';
import type { Commission, CommissionStatus } from '../types';

type CommissionOrderingColumn =
  | 'status'
  | 'won_date'
  | 'realized_date'
  | 'projected_base_amount'
  | 'earned_base_amount';

const VIEW_MODES: readonly ViewModeOption<CommissionsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

const COMMISSION_MANAGE_ROLES = new Set(['founder', 'super_admin', 'accountant']);
const usd = (v: string | null | undefined) => formatMoney(v, 'USD') ?? '—';
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

/** The amount to show: projected while pending, the realized figure otherwise. */
function displayAmount(c: Commission): string {
  return c.status === 'pending' ? c.projected_base_amount : c.earned_base_amount;
}

export function CommissionsListPage() {
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canManage = role != null && COMMISSION_MANAGE_ROLES.has(role);

  const view = useCommissionsUi((s) => s.view);
  const setView = useCommissionsUi((s) => s.setView);
  const ordering = useCommissionsUi((s) => s.ordering);
  const setOrdering = useCommissionsUi((s) => s.setOrdering);

  const [owner, setOwner] = useState('');
  const [status, setStatus] = useState('all');
  const [wonFrom, setWonFrom] = useState('');
  const [wonTo, setWonTo] = useState('');
  const [page, setPage] = useState(1);

  const filtersActive =
    owner !== '' || status !== 'all' || wonFrom !== '' || wonTo !== '';

  function resetFilters() {
    setOwner('');
    setStatus('all');
    setWonFrom('');
    setWonTo('');
    setPage(1);
  }

  const queryParams = {
    page,
    salesperson: owner ? Number(owner) : undefined,
    status: status === 'all' ? undefined : (status as CommissionStatus),
    won_after: wonFrom || undefined,
    won_before: endOfDayParam(wonTo),
    ordering,
  };
  const { data, isPending, isError, error, isFetching } = useCommissions(queryParams);
  const { data: summary } = useCommissionSummary(queryParams);
  const payOut = useMarkCommissionPaidOut();
  const voidIt = useVoidCommission();
  const del = useDeleteCommission();

  const results = data?.results ?? [];
  const open = (c: Commission) => navigate(`/crm/deals/${c.deal_public_id}`);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Commissions</h1>
      <p className="text-muted-foreground text-sm">
        Sales commissions per deal — projected when a deal is won, earned once the client pays the
        linked invoice.
      </p>

      {/* Org-wide totals (respect the active filters). */}
      <div className="grid gap-3 sm:grid-cols-3">
        <TotalCard label="Pending (projected)" value={usd(summary?.pending_projected_base)} muted />
        <TotalCard label="Earned" value={usd(summary?.earned_base)} />
        <TotalCard label="Paid out" value={usd(summary?.paid_out_base)} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <MemberSelect
            value={owner}
            onChange={(v) => {
              setPage(1);
              setOwner(v);
            }}
            placeholder="All sales"
            queryKey="commissions-salesperson-filter"
            roles={CRM_OWNER_ROLES}
            clearable
            triggerClassName="h-9 w-auto min-w-28"
          />
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {COMMISSION_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateRangeFilter
            label="Won"
            idPrefix="commissions-won"
            from={wonFrom}
            to={wonTo}
            onFromChange={(v) => {
              setPage(1);
              setWonFrom(v);
            }}
            onToChange={(v) => {
              setPage(1);
              setWonTo(v);
            }}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              void exportCsv(COMMISSIONS_BASE, {
                salesperson: owner ? Number(owner) : undefined,
                status: status === 'all' ? undefined : status,
                won_after: wonFrom || undefined,
                won_before: endOfDayParam(wonTo),
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export commissions as CSV"
            title="Export commissions as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      <p className="text-muted-foreground text-xs">
        <span className="italic">Projected</span> = rate × deal estimate (while pending); once the
        client pays, the row shows the <strong>actual</strong> commission (rate × amount received).
      </p>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <CommissionsSkeleton />
      ) : results.length === 0 ? (
        <CommissionsTable
          rows={[]}
          ordering={ordering}
          onOrderingChange={setOrdering}
          canManage={false}
          busy={false}
          onOpen={() => undefined}
          onPayOut={() => undefined}
          onVoid={() => undefined}
          onDelete={() => undefined}
        />
      ) : view === 'table' ? (
        <CommissionsTable
          rows={results}
          ordering={ordering}
          onOrderingChange={setOrdering}
          canManage={canManage}
          busy={payOut.isPending || voidIt.isPending || del.isPending}
          onOpen={open}
          onPayOut={(c) => payOut.mutate(c.public_id)}
          onVoid={(c) => voidIt.mutate(c.public_id)}
          onDelete={(c) => del.mutate(c.public_id)}
        />
      ) : view === 'list' ? (
        <CommissionsList rows={results} onOpen={open} />
      ) : view === 'cards' ? (
        <CommissionsCards rows={results} onOpen={open} />
      ) : (
        <CommissionsGroupedByStatus rows={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'commission' : 'commissions'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TotalCard({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${muted ? 'text-muted-foreground italic' : ''}`}>
        {value}
      </p>
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  rows: Commission[];
  onOpen: (c: Commission) => void;
}

interface CommissionsTableProps extends ListProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  canManage: boolean;
  busy: boolean;
  onPayOut: (c: Commission) => void;
  onVoid: (c: Commission) => void;
  onDelete: (c: Commission) => void;
}

function AmountCell({ c }: { c: Commission }) {
  const projected = c.status === 'pending';
  return (
    <span
      className={`tabular-nums ${projected ? 'text-muted-foreground italic' : ''} ${c.status === 'void' ? 'line-through' : ''}`}
      title={projected ? 'Projected — estimated until the client pays' : 'Actual — from money received'}
    >
      {usd(displayAmount(c))}
      {projected ? (
        <Badge variant="outline" className="ml-2 text-[10px]">
          Projected
        </Badge>
      ) : null}
    </span>
  );
}

function CommissionsTable({
  rows,
  ordering,
  onOrderingChange,
  canManage,
  busy,
  onOpen,
  onPayOut,
  onVoid,
  onDelete,
}: CommissionsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Deal</TableHead>
            <TableHead className="hidden sm:table-cell">Salesperson</TableHead>
            <TableHead className="text-right">Rate</TableHead>
            <TableHead className="text-right">Commission</TableHead>
            <SortableTableHead<CommissionOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<CommissionOrderingColumn>
              column="won_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Won
            </SortableTableHead>
            {canManage ? <TableHead className="w-32 text-right">Actions</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canManage ? 7 : 6} className="text-muted-foreground py-10 text-center">
                No commissions found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((c) => (
              <TableRow key={c.id} className={c.status === 'void' ? 'opacity-50' : ''}>
                <TableCell>
                  <button type="button" onClick={() => onOpen(c)} className="font-medium hover:underline">
                    {c.deal_name || '—'}
                  </button>
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {c.salesperson_name || '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">{Number(c.rate_pct)}%</TableCell>
                <TableCell className="text-right">
                  <AmountCell c={c} />
                </TableCell>
                <TableCell>
                  <Badge variant={commissionStatusVariant(c.status)}>
                    {commissionStatusLabel(c.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {fmtDate(c.won_date)}
                </TableCell>
                {canManage ? (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {c.status === 'earned' ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => onPayOut(c)}>
                          Pay out
                        </Button>
                      ) : null}
                      {c.status !== 'void' && c.status !== 'paid_out' ? (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onVoid(c)}>
                          Void
                        </Button>
                      ) : null}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={busy}
                            aria-label={`Delete commission for ${c.deal_name || 'deal'}`}
                            title="Delete"
                          >
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this commission?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Permanently removes the commission row. This cannot be undone — use
                              Void to cancel without deleting the record.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(c)} disabled={busy}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CommissionsCards({ rows, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((c) => (
        <Card
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(c);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{c.deal_name || '—'}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={commissionStatusVariant(c.status)}>{commissionStatusLabel(c.status)}</Badge>
            <span className="text-muted-foreground">{c.salesperson_name || '—'}</span>
            <span className="ml-auto">
              <AmountCell c={c} />
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CommissionsList({ rows, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onOpen(c)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <Badge variant={commissionStatusVariant(c.status)} className="shrink-0">
              {commissionStatusLabel(c.status)}
            </Badge>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.deal_name || '—'}</span>
            <span className="text-muted-foreground hidden text-xs sm:inline">
              {c.salesperson_name || '—'}
            </span>
            <span className="text-xs">
              <AmountCell c={c} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function CommissionsGroupedByStatus({ rows, onOpen }: ListProps) {
  const groups = useMemo(
    () =>
      COMMISSION_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((c) => c.status === s.value),
      })),
    [rows],
  );

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`commissions-status-${key}`}>
            <h2
              id={`commissions-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <CommissionsCards rows={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function CommissionsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
